import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  CheckCircle,
  UserPlus,
  FolderPlus,
  Clock,
  DollarSign,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';

export interface Activity {
  id: string;
  type: 'contract' | 'project' | 'client' | 'time' | 'payment' | 'other';
  title: string;
  description: string;
  timestamp: string;
  entityId?: string;
  entityType?: 'client' | 'project' | 'proposal';
}

interface ActivityFeedProps {
  activities: Activity[];
}

const activityIcons = {
  contract: FileText,
  project: FolderPlus,
  client: UserPlus,
  time: Clock,
  payment: DollarSign,
  other: CheckCircle,
};

const activityColors = {
  contract: 'bg-blue-100 text-blue-600',
  project: 'bg-green-100 text-green-600',
  client: 'bg-purple-100 text-purple-600',
  time: 'bg-orange-100 text-orange-600',
  payment: 'bg-emerald-100 text-emerald-600',
  other: 'bg-gray-100 text-gray-600',
};

const activityBadgeColors = {
  contract: 'bg-blue-50 text-blue-700 border-blue-200',
  project: 'bg-green-50 text-green-700 border-green-200',
  client: 'bg-purple-50 text-purple-700 border-purple-200',
  time: 'bg-orange-50 text-orange-700 border-orange-200',
  payment: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  other: 'bg-gray-50 text-gray-700 border-gray-200',
};

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function ActivityFeed({ activities }: ActivityFeedProps) {
  return (
    <Card className="p-6 border-gray-100 shadow-card">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
        <p className="text-sm text-gray-500 mt-1">Latest updates from your team</p>
      </div>
      <div className="space-y-4">
        {activities.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">No recent activity</p>
          </div>
        ) : (
          activities.map((activity) => {
            const Icon = activityIcons[activity.type] || CheckCircle;
            const colorClass = activityColors[activity.type];
            const badgeClass = activityBadgeColors[activity.type];

            const content = (
              <div className="flex gap-3 group cursor-pointer hover:bg-gray-50 -mx-2 px-2 py-2 rounded-lg transition-colors">
                <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0', colorClass)}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-medium text-gray-900 group-hover:text-primary-600 transition-colors">
                      {activity.title}
                    </p>
                    <Badge variant="outline" className={cn('text-xs flex-shrink-0', badgeClass)}>
                      {activity.type}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-1">{activity.description}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatTimestamp(activity.timestamp)}</p>
                </div>
              </div>
            );

            if (activity.entityId && activity.entityType) {
              const link = `/${activity.entityType}s/${activity.entityId}`;
              return (
                <Link key={activity.id} to={link}>
                  {content}
                </Link>
              );
            }

            return <div key={activity.id}>{content}</div>;
          })
        )}
      </div>
      {activities.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <Link
            to="/activity"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
          >
            View all activity →
          </Link>
        </div>
      )}
    </Card>
  );
}
